<template>
  <div class="chat-agent">
    <div class="chat-messages" ref="messagesContainer">
        <!-- Welcome message -->
        <div v-if="messages.length === 0" class="chat-welcome">
          <p>Hi! I can help you explore your POs and inbound shipments. Try asking:</p>
          <ul>
            <li @click="submitSuggestion('Show me all open POs')">Show me all open POs</li>
            <li @click="submitSuggestion('Which shipments are in transit?')">Which shipments are in transit?</li>
            <li @click="submitSuggestion('Show me the items in shipment 42')">Show me the items in shipment #42</li>
          </ul>
        </div>

        <!-- Message list -->
        <div v-for="(msg, i) in messages" :key="i" :class="'chat-msg chat-msg--' + msg.role">
          <div class="chat-bubble">
            <!-- Tool call badges -->
            <div v-if="msg.toolCalls && msg.toolCalls.length" class="chat-tool-badges">
              <span v-for="(tc, j) in msg.toolCalls" :key="j" class="chat-tool-badge">📊 {{ tc.name }}</span>
            </div>
            <!-- Text before structured data -->
            <div v-if="msg.textBefore" class="chat-text" v-html="renderMarkdown(msg.textBefore)"></div>
            <!-- Structured table output -->
            <div v-if="msg.structuredData && msg.structuredData.table" class="chat-table-wrapper">
              <table class="table is-narrow is-hoverable is-fullwidth">
                <thead>
                  <tr>
                    <th v-for="(h, hi) in msg.structuredData.table.headers" :key="hi">{{ h }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, ri) in msg.structuredData.table.rows" :key="ri">
                    <td v-for="(cell, ci) in row" :key="ci">{{ cell }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <!-- Structured chart output -->
            <div v-if="msg.structuredData && msg.structuredData.chart" class="chat-chart-wrapper">
              <v-chart :option="buildChartOption(msg.structuredData.chart)" autoresize style="height: 250px;" />
            </div>
            <!-- Text after structured data -->
            <div v-if="msg.textAfter" class="chat-text" v-html="renderMarkdown(msg.textAfter)"></div>
            <!-- Plain text only (no structured data) -->
            <div v-if="!msg.structuredData && !msg.textBefore" class="chat-text" v-html="renderMarkdown(msg.text)"></div>
          </div>
        </div>

        <!-- Loading indicator -->
        <div v-if="isLoading" class="chat-msg chat-msg--assistant">
          <div class="chat-bubble">
            <div v-if="activeToolCalls.length" class="chat-tool-status">
              <div v-for="(tc, i) in activeToolCalls" :key="i" class="chat-tool-call">
                <span :class="'chat-tool-dot ' + tc.status"></span>
                <span class="chat-tool-name">{{ tc.status === 'calling' ? '⏳' : '✓' }} {{ tc.name }}</span>
              </div>
            </div>
            <p class="chat-typing">Thinking...</p>
          </div>
        </div>
      </div>

      <!-- Input area -->
      <div class="chat-input-area">
        <textarea
          class="chat-input"
          v-model="inputValue"
          @keydown.enter.exact.prevent="handleSubmit"
          placeholder="Ask about your purchase orders and inbound shipments..."
          :disabled="isLoading"
          rows="1"
        ></textarea>
        <button class="chat-send" :disabled="!inputValue.trim() || isLoading" @click="handleSubmit">Send</button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import showdown from 'showdown'

// ─── TOOL DEFINITIONS ────────────────────────────────────────────────────────
// These are sent to Claude so it knows what APIs it can call.
const TOOLS = [
  {
    name: 'get_all_inbounds',
    description: 'Fetch all inbound shipments. Returns a list of inbound shipment objects with fields: id, shipmentNumber, bookingId, vendorName, locationName, shipmentStatus (e.g. toBeShipped, inTransit, partiallyReceived, received, closed, Draft), mbl, vessel, containers, expectedShippingDate, expectedDeliveryDate, lastModifiedDate, itemsInfo.',
    input_schema: {
      type: 'object',
      properties: {},
      required: [],
    },
  },
  {
    name: 'get_inbound_items',
    description: 'Fetch detailed line items (with purchase order info) for a specific inbound shipment. Returns items with: purchaseOrderName, itemId, shipmentItemName, shipmentItemDescription, quantityExpected, containerInfo, poItem (rate, dutyPercentage, dutyPercentageCanada), and the PO location info.',
    input_schema: {
      type: 'object',
      properties: {
        inboundId: {
          type: 'number',
          description: 'The ID of the inbound shipment to get items for',
        },
      },
      required: ['inboundId'],
    },
  },
  {
    name: 'get_all_purchase_orders',
    description: 'Fetch all purchase orders (POs). Returns a list of PO objects with fields: id, tranId (PO number), subsidiaryName, locationName, locationId, vendorName, statusId (e.g. pendingReceipt, partiallyReceived, pendingBilling, fullyBilled, closed), approvalStatusName, memo, lastModifiedDate. Note: one PO can have items shipped across multiple inbound shipments, and one inbound shipment can contain items from multiple POs.',
    input_schema: {
      type: 'object',
      properties: {},
      required: [],
    },
  },
  {
    name: 'get_purchase_order_items',
    description: 'Fetch line items for a specific purchase order, including linked inbound shipment info. Returns items with: line, itemId, itemName, quantityAvailable (remaining qty), rate (unit price), quantity (PO qty), dutyPercentage, dutyPercentageCanada, and inbounds array. Each inbound entry has: inbound (id, shipmentNumber, bookingId, expectedDeliveryDate, shipmentStatus) and inboundItem (quantityExpected, containerInfo). A PO item can be split across multiple inbound shipments.',
    input_schema: {
      type: 'object',
      properties: {
        purchaseOrderId: {
          type: 'number',
          description: 'The ID of the purchase order to get items for',
        },
      },
      required: ['purchaseOrderId'],
    },
  },
  {
    name: 'run_js_code',
    description: 'Execute JavaScript code against previously fetched API data stored in memory. Use this to filter, aggregate, join, or compute over data already fetched by other tools — avoiding re-fetching or sending large data back to you. Available variables in the data store are listed in each tool_result under "savedAs". Write a function body (not a full function declaration) that receives a "data" object containing the requested variables and returns a JSON-serializable result. Example: if "inbounds" is in the store, use data.inbounds to access it.',
    input_schema: {
      type: 'object',
      properties: {
        code: {
          type: 'string',
          description: 'JavaScript function body. Receives "data" as argument containing requested variables. Must return a value. Example: "return data.inbounds.filter(ib => ib.shipmentStatus === \'inTransit\').length"',
        },
        variables: {
          type: 'array',
          items: { type: 'string' },
          description: 'List of dataStore variable names to pass into the function as data.<name>',
        },
      },
      required: ['code', 'variables'],
    },
  },
]

const SYSTEM_PROMPT = `You are a helpful assistant for managing inbound shipments (logistics/supply chain).
You have access to tools that fetch inbound shipment data and their line items.

When the user asks a question:
1. Decide which tool(s) to call to get the data you need.
2. After receiving tool results, the data is automatically saved in a browser-side data store. The tool_result will tell you the variable name under "savedAs".
3. If you need to filter, aggregate, join, or compute across fetched data, use the run_js_code tool. Write JavaScript that accesses the saved variables via the "data" argument.
4. When you have enough information, respond with a final answer.

For run_js_code:
- You receive a "data" object with the variables you request. E.g., if you request variables: ["inbounds"], your code can use data.inbounds.
- Your code is a function body — use "return" to return the result.
- The result must be JSON-serializable (objects, arrays, numbers, strings, booleans, null).
- Example: { code: "return data.inbounds.filter(ib => ib.shipmentStatus === 'inTransit').length", variables: ["inbounds"] }

For your final answer:
- If showing tabular data, format it as a JSON object with key "table" containing { "headers": [...], "rows": [[...], ...] }
- If the user asks for a chart, format it as a JSON object with key "chart" containing { "type": "bar|line|pie", "title": "...", "categories": [...], "series": [{ "name": "...", "data": [...] }] }
- For plain text answers, just respond normally.
- Wrap structured output in a JSON code block: \`\`\`json\\n{...}\\n\`\`\`

Keep tables concise — show the most relevant columns. If there are many rows, summarize or show top results unless the user asks for all.`

const MAX_ITERATIONS = 8

export default {
  name: 'chat-agent',
  props: ['claudeKey'],
  data () {
    return {
      inputValue: '',
      isLoading: false,
      messages: [],         // { role, text, structuredData?, toolCalls? }
      conversationHistory: [], // raw messages for multi-turn (sent to Claude)
      activeToolCalls: [],
      dataStore: {},        // Saved API results keyed by name (e.g. 'inbounds', 'items_42')
      mdConverter: new showdown.Converter({ tables: true }),
    }
  },
  computed: {
    server () {
      return this.$store.state.config.server
    },
    token () {
      return this.$store.state.user.token
    },
  },
  watch: {
    messages () {
      this.$nextTick(() => {
        this.scrollToBottom()
      })
    },
  },
  methods: {
    // ─── UI METHODS ────────────────────────────────────────────────────────────
    scrollToBottom () {
      var container = this.$refs.messagesContainer
      if (container) {
        container.scrollTop = container.scrollHeight
      }
    },

    renderMarkdown (text) {
      if (!text) return ''
      // Strip the json code block for display (structured data handled separately)
      var cleaned = text.replace(/```json\s*\n[\s\S]*?\n```/g, '').trim()
      return this.mdConverter.makeHtml(cleaned)
    },

    getTextBefore (text) {
      if (!text) return ''
      var match = text.match(/```json\s*\n[\s\S]*?\n```/)
      if (!match) return ''
      var before = text.substring(0, match.index).trim()
      return before || ''
    },

    getTextAfter (text) {
      if (!text) return ''
      var match = text.match(/```json\s*\n[\s\S]*?\n```/)
      if (!match) return ''
      var after = text.substring(match.index + match[0].length).trim()
      return after || ''
    },

    submitSuggestion (text) {
      this.inputValue = text
      this.handleSubmit()
    },

    buildChartOption (chartData) {
      // Build ECharts option from LLM-provided chart spec
      var option = {
        title: { text: chartData.title || '', textStyle: { fontSize: 13 } },
        tooltip: { trigger: 'axis' },
        grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
      }

      if (chartData.type === 'pie') {
        option.tooltip = { trigger: 'item' }
        option.series = [{
          type: 'pie',
          radius: '60%',
          data: (chartData.categories || []).map(function (cat, i) {
            return { name: cat, value: chartData.series[0].data[i] }
          }),
        }]
      } else {
        option.xAxis = { type: 'category', data: chartData.categories || [] }
        option.yAxis = { type: 'value' }
        option.series = (chartData.series || []).map(function (s) {
          return { name: s.name, type: chartData.type || 'bar', data: s.data }
        })
        if (chartData.series && chartData.series.length > 1) {
          option.legend = { data: chartData.series.map(function (s) { return s.name }) }
        }
      }
      return option
    },

    // ─── TOOL EXECUTOR ─────────────────────────────────────────────────────────
    // Maps tool names to actual API calls using the app's auth token.
    // Results are trimmed to essential fields to stay within LLM token limits.
    // API results are also saved to dataStore for use by run_js_code.
    async executeTool (toolName, toolInput) {
      var headers = { Authorization: 'Token ' + this.token }

      if (toolName === 'get_all_inbounds') {
        var resp = await axios.get(this.server + '/myapp/get-all-inbounds/', { headers: headers })
        var trimmed = resp.data.map(function (ib) {
          return {
            id: ib.id,
            shipmentNumber: ib.shipmentNumber,
            bookingId: ib.bookingId,
            vendorName: ib.vendorName,
            locationName: ib.locationName,
            shipmentStatus: ib.shipmentStatus,
            mbl: ib.mbl,
            vessel: ib.vessel,
            containers: ib.containers,
            expectedShippingDate: ib.expectedShippingDate,
            expectedDeliveryDate: ib.expectedDeliveryDate,
            lastModifiedDate: ib.lastModifiedDate,
          }
        })
        // Save full data to dataStore for run_js_code access
        this.$set(this.dataStore, 'inbounds', resp.data)
        return { savedAs: 'inbounds', total: trimmed.length, inbounds: trimmed }
      }

      if (toolName === 'get_inbound_items') {
        var resp2 = await axios.get(
          this.server + '/myapp/get-inbound-items-with-pos/' + toolInput.inboundId,
          { headers: headers }
        )
        var trimmedItems = resp2.data.map(function (item) {
          return {
            purchaseOrderName: item.purchaseOrderName,
            itemId: item.itemId,
            shipmentItemName: item.shipmentItemName,
            shipmentItemDescription: item.shipmentItemDescription,
            quantityExpected: item.quantityExpected,
            containerInfo: item.containerInfo,
            unitPrice: item.poItem && item.poItem.rate,
            dutyPercentage: item.poItem && item.poItem.dutyPercentage,
          }
        })
        // Save full data to dataStore
        var storeKey = 'items_' + toolInput.inboundId
        this.$set(this.dataStore, storeKey, resp2.data)
        return { savedAs: storeKey, inboundId: toolInput.inboundId, total: trimmedItems.length, items: trimmedItems }
      }

      if (toolName === 'get_all_purchase_orders') {
        var resp3 = await axios.get(this.server + '/myapp/get-all-purchase-orders/', { headers: headers })
        var trimmedPos = resp3.data.map(function (p) {
          return {
            id: p.id,
            tranId: p.tranId,
            subsidiaryName: p.subsidiaryName,
            locationName: p.locationName,
            vendorName: p.vendorName,
            statusId: p.statusId,
            approvalStatusName: p.approvalStatusName,
            memo: p.memo,
            lastModifiedDate: p.lastModifiedDate,
          }
        })
        this.$set(this.dataStore, 'purchaseOrders', resp3.data)
        return { savedAs: 'purchaseOrders', total: trimmedPos.length, purchaseOrders: trimmedPos }
      }

      if (toolName === 'get_purchase_order_items') {
        var resp4 = await axios.get(
          this.server + '/myapp/get-purchase-order-items-with-inbounds/' + toolInput.purchaseOrderId,
          { headers: headers }
        )
        var trimmedPoItems = resp4.data.map(function (pi) {
          return {
            line: pi.line,
            itemId: pi.itemId,
            itemName: pi.itemName,
            quantityAvailable: pi.quantityAvailable,
            rate: pi.rate,
            quantity: pi.quantity,
            dutyPercentage: pi.dutyPercentage,
            inbounds: (pi.inbounds || []).map(function (ib) {
              if (!ib) return null
              return {
                shipmentNumber: ib.inbound && ib.inbound.shipmentNumber,
                bookingId: ib.inbound && ib.inbound.bookingId,
                shipmentStatus: ib.inbound && ib.inbound.shipmentStatus,
                expectedDeliveryDate: ib.inbound && ib.inbound.expectedDeliveryDate,
                quantityExpected: ib.inboundItem && ib.inboundItem.quantityExpected,
                containerInfo: ib.inboundItem && ib.inboundItem.containerInfo,
              }
            }).filter(Boolean),
          }
        })
        var poStoreKey = 'poItems_' + toolInput.purchaseOrderId
        this.$set(this.dataStore, poStoreKey, resp4.data)
        return { savedAs: poStoreKey, purchaseOrderId: toolInput.purchaseOrderId, total: trimmedPoItems.length, items: trimmedPoItems }
      }

      if (toolName === 'run_js_code') {
        return this.executeJsCode(toolInput.code, toolInput.variables)
      }

      return { error: 'Unknown tool: ' + toolName }
    },

    // ─── JS CODE EXECUTOR ──────────────────────────────────────────────────────
    // Runs LLM-generated JavaScript against saved dataStore variables.
    // The code is a function body that receives "data" and must return a value.
    executeJsCode (code, variables) {
      try {
        // Build the data object from requested variables
        var data = {}
        for (var i = 0; i < variables.length; i++) {
          var varName = variables[i]
          if (this.dataStore[varName] !== undefined) {
            data[varName] = this.dataStore[varName]
          } else {
            return { error: 'Variable "' + varName + '" not found in data store. Available: ' + Object.keys(this.dataStore).join(', ') }
          }
        }

        // Create and execute the function
        var fn = new Function('data', code) // eslint-disable-line no-new-func
        var result = fn(data)

        // Ensure result is JSON-serializable
        return { result: JSON.parse(JSON.stringify(result)) }
      } catch (err) {
        return { error: 'JavaScript execution error: ' + err.message }
      }
    },

    // Cap serialized tool result to prevent exceeding token limits
    capToolResult (data) {
      var json = JSON.stringify(data)
      var MAX_CHARS = 10000 // 2.5K tokens, safe for a single tool result
      if (json.length <= MAX_CHARS) return json
      // Truncate and add summary
      var truncated = json.substring(0, MAX_CHARS)
      return truncated + '\n... [TRUNCATED — result too large. Total items: ' + (data.total || 'unknown') + '. Ask user to filter or be more specific.]'
    },

    // ─── LLM CALL ──────────────────────────────────────────────────────────────
    // Calls Claude directly from the browser using the customer-provided API key.
    async callClaude (messages) {
      var resp = await axios.post(
        'https://api.anthropic.com/v1/messages',
        {
          model: 'claude-sonnet-4-6',
          system: SYSTEM_PROMPT,
          messages: messages,
          tools: TOOLS,
          max_tokens: 4096,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': this.claudeKey,
            'anthropic-version': '2023-06-01',
            'anthropic-dangerous-direct-browser-access': 'true',
          },
        }
      )
      return resp.data
    },

    // ─── AGENT ORCHESTRATOR ────────────────────────────────────────────────────
    // The core agentic loop:
    // 1. Send messages + tools to Claude
    // 2. If Claude responds with tool_use → execute tools, append results, loop
    // 3. If Claude responds with end_turn (no tool_use) → final answer, exit loop
    async runAgent (userMessage) {
      var messages = this.conversationHistory.concat([
        { role: 'user', content: userMessage },
      ])

      var iterations = 0

      while (iterations < MAX_ITERATIONS) {
        iterations++

        // Step 1: Call Claude
        var llmResponse = await this.callClaude(messages)
        var content = llmResponse.content
        var stopReason = llmResponse.stop_reason

        // Step 2: Separate tool_use blocks from text blocks
        var toolUseBlocks = content.filter(function (b) { return b.type === 'tool_use' })
        var textBlocks = content.filter(function (b) { return b.type === 'text' })

        // Step 3: If no tool calls → FINAL ANSWER
        if (stopReason === 'end_turn' || toolUseBlocks.length === 0) {
          var finalText = textBlocks.map(function (b) { return b.text }).join('\n')
          var structuredData = this.parseStructuredOutput(finalText)

          return {
            response: finalText,
            structuredData: structuredData,
            history: messages.concat([{ role: 'assistant', content: content }]),
          }
        }

        // Step 4: TOOL USE — execute each tool and loop back
        messages.push({ role: 'assistant', content: content })

        var toolResults = []
        for (var i = 0; i < toolUseBlocks.length; i++) {
          var block = toolUseBlocks[i]

          // Notify UI
          this.activeToolCalls.push({ name: block.name, status: 'calling' })

          // Execute
          var result
          try {
            result = await this.executeTool(block.name, block.input)
          } catch (err) {
            result = { error: 'API call failed: ' + (err.message || 'unknown') }
          }

          // Mark done in UI
          this.$set(this.activeToolCalls, this.activeToolCalls.length - 1, { name: block.name, status: 'done' })

          toolResults.push({
            type: 'tool_result',
            tool_use_id: block.id,
            content: this.capToolResult(result),
          })
        }

        // Append tool results and loop
        messages.push({ role: 'user', content: toolResults })
      }

      // Safety: max iterations
      return {
        response: 'I reached the maximum number of steps. Please try a more specific question.',
        structuredData: null,
        history: messages,
      }
    },

    // Parse ```json code blocks for table/chart structured data
    parseStructuredOutput (text) {
      var match = text.match(/```json\s*\n([\s\S]*?)\n```/)
      if (!match) return null
      try {
        var parsed = JSON.parse(match[1])
        if (parsed.table || parsed.chart) return parsed
        return null
      } catch (e) {
        return null
      }
    },

    // ─── SUBMIT HANDLER ────────────────────────────────────────────────────────
    async handleSubmit () {
      var userMessage = this.inputValue.trim()
      if (!userMessage || this.isLoading) return

      // Add user message to chat
      this.messages.push({ role: 'user', text: userMessage })
      this.inputValue = ''
      this.isLoading = true
      this.activeToolCalls = []

      try {
        var result = await this.runAgent(userMessage)

        // Add assistant response
        this.messages.push({
          role: 'assistant',
          text: result.response,
          structuredData: result.structuredData,
          textBefore: result.structuredData ? this.getTextBefore(result.response) : null,
          textAfter: result.structuredData ? this.getTextAfter(result.response) : null,
          toolCalls: this.activeToolCalls.slice(),
        })
        this.conversationHistory = result.history
      } catch (err) {
        this.messages.push({
          role: 'assistant',
          text: 'Error: ' + (err.message || 'Something went wrong'),
        })
      }

      this.isLoading = false
      this.activeToolCalls = []
    },
  },
}
</script>

<style scoped>
.chat-agent {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
}

.chat-header {
  display: flex;
  align-items: center;
  padding: 14px 20px;
  background: #363636;
  color: white;
}
.chat-title {
  font-weight: 600;
  font-size: 16px;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

.chat-welcome {
  color: #666;
  font-size: 14px;
  max-width: 600px;
}
.chat-welcome ul {
  margin-top: 8px;
  padding-left: 16px;
}
.chat-welcome li {
  margin: 6px 0;
  cursor: pointer;
  color: #3273dc;
}
.chat-welcome li:hover {
  text-decoration: underline;
}

.chat-msg {
  margin-bottom: 16px;
  display: flex;
}
.chat-msg--user {
  justify-content: flex-end;
}
.chat-msg--assistant {
  justify-content: flex-start;
}

.chat-bubble {
  max-width: 80%;
  padding: 12px 16px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 1.5;
}
.chat-msg--user .chat-bubble {
  background: #3273dc;
  color: white;
}
.chat-msg--assistant .chat-bubble {
  background: #f5f5f5;
  color: #1a1a1a;
}

.chat-text {
  margin: 0;
  word-break: break-word;
}
.chat-text >>> p {
  margin: 0 0 0.5em 0;
}
.chat-text >>> p:last-child {
  margin-bottom: 0;
}

.chat-tool-badges {
  margin-bottom: 8px;
}
.chat-tool-badge {
  display: inline-block;
  background: #e8f4fd;
  color: #3273dc;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  margin-right: 4px;
}

.chat-tool-status {
  margin-bottom: 6px;
}
.chat-tool-call {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #666;
  margin: 3px 0;
}
.chat-tool-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 8px;
}
.chat-tool-dot.calling {
  background: #ffdd57;
  animation: pulse 1s infinite;
}
.chat-tool-dot.done {
  background: #48c774;
}
.chat-tool-name {
  font-family: monospace;
  font-size: 11px;
}

.chat-typing {
  color: #999;
  font-size: 12px;
  font-style: italic;
  margin: 0;
}

.chat-table-wrapper {
  overflow-x: auto;
  margin: 8px 0;
}
.chat-table-wrapper table {
  font-size: 13px;
}

.chat-chart-wrapper {
  margin: 8px 0;
  width: 100%;
  min-height: 300px;
}

.chat-input-area {
  display: flex;
  padding: 16px 20px;
  gap: 10px;
}
.chat-input {
  flex: 1;
  border: 1px solid #dbdbdb;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 14px;
  resize: none;
  outline: none;
}
.chat-input:focus {
  border-color: #3273dc;
}
.chat-input:disabled {
  background: #f9f9f9;
}
.chat-send {
  background: #3273dc;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  font-size: 14px;
  cursor: pointer;
}
.chat-send:hover:not(:disabled) {
  background: #2366d1;
}
.chat-send:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}
</style>
